'use client'

import * as React from 'react'
import { Button } from '@repo/design-system-cf/components/button'
import { IconChevronLeft, IconChevronRight } from '@repo/design-system-cf/components/icons'
import { GptImageBanner as SharedGptImageBanner } from '@repo/studio-components-gpt-image-banner/gpt-image-banner'
import type {
  GptImageBannerImageRenderer,
  GptImageBannerLinkRenderer,
  GptImageBannerProps,
} from '@repo/studio-components-gpt-image-banner/gpt-image-banner'
import { Link } from '@repo/web-ui/components/link'
import { AppRoutes } from '@repo/web-utils/app-routes'
import { LFS_URL } from '@repo/web-utils/env'
import Image from 'next/image'
import { useTranslations } from 'next-intl'
import { tw } from 'utils/classnames'

const GPT_IMAGE_FLOW_URL = `${AppRoutes.STUDIO_TOOL_FLOW}?model=OpenAigptImage2`
const GPT_IMAGE_LFS_URL = LFS_URL ?? ''

export function GptImageBanner({ className }: { className?: string }) {
  const t = useTranslations('pages.home.gpt_image_banner.promo')
  const carouselT = useTranslations('pages.home.landscape_slider')

  const renderLink = React.useCallback<GptImageBannerLinkRenderer>(
    ({ children }) => (
      <Link href={GPT_IMAGE_FLOW_URL} target="_blank" rel="noopener noreferrer">
        {children}
      </Link>
    ),
    [],
  )

  const renderImage = React.useCallback<GptImageBannerImageRenderer>(
    ({ src, className: imageClassName }) => (
      <Image src={src} alt="" unoptimized fill sizes="232px" className={imageClassName} />
    ),
    [],
  )

  const renderCarouselButton = React.useCallback<
    NonNullable<GptImageBannerProps['renderCarouselButton']>
  >(
    ({ variant, buttonHeight, onClick }) => (
      <div
        className={tw(
          'absolute top-0 w-24 flex items-center pointer-events-none',
          variant === 'left' && 'left-0 justify-start',
          variant === 'right' && 'right-0 justify-end',
        )}
        style={{ height: buttonHeight }}
      >
        <Button
          variant="primary"
          size="small"
          className="pointer-events-auto mx-4"
          onClick={onClick}
          aria-label={variant === 'left' ? carouselT('scroll_left') : carouselT('scroll_right')}
          iconLeft={variant === 'left' ? <IconChevronLeft /> : <IconChevronRight />}
        />
      </div>
    ),
    [carouselT],
  )

  return (
    <SharedGptImageBanner
      className={className}
      lfsUrl={GPT_IMAGE_LFS_URL}
      copy={{
        title: t('title'),
        description: t('description'),
        button: t('button'),
      }}
      renderLink={renderLink}
      renderImage={renderImage}
      layoutClassName="grid grid-cols-1 md:grid-cols-[400px_1fr] gap-3"
      gap={12}
      shadowColor="text-cf-grey-50 w-12"
      renderCarouselButton={renderCarouselButton}
    />
  )
}
